import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  Signal,
  computed,
  effect,
  inject,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Dictionary } from '@ngrx/entity/src/models';
import { TranslatePipe } from '@ngx-translate/core';
import { MatTooltip } from '@angular/material/tooltip';
import { Observable, bufferTime, distinctUntilChanged, filter, map, switchMap, take, tap } from 'rxjs';
import moment from 'moment-timezone';
import { routerSegments } from '@consts/routes';
import { IntelligenceModels } from '@models/intelligence.model';
import { WallGridViewMode, WallV2Model, WallViewTab } from '@models/wall-v2.model';
import { CameraSelectors } from '@states/camera/camera.selector-types';
import { LocationSelectors } from '@states/location/location.selector-types';
import { MultiPlaybackActions } from '@states/multi-playback/multi-playback.action-types';
import { OrganizationSelectors } from '@states/organization/organization.selector-types';
import { SharedActions } from '@states/shared/shared.action-types';
import { WallV2Actions } from '@states/wall-v2/wall-v2.action-types';
import { WallV2Selectors } from '@states/wall-v2/wall-v2.selector-types';
import type { WallV2State } from '@states/wall-v2/wall-v2.reducer';
import { PreloaderColor, UiPreloaderComponent } from '@ui';
import { EdgeCamera } from '../../../../../../cameras/camera.model';
import { LocationModel } from '../../../../../../locations/location.model';
import { AlertEntry } from '../../../../../../development/alerts.service';
import { SocketMainService } from '../../../../../../socket/socket-main.service';
import { SocketEvents } from '../../../../../../socket/socket.model';
import { WallV2Module } from '../../../../../../pages/wall-v2/wall-v2.module';
import { IntelligenceResultsService } from '../../../../../../services/intelligence-results.service';
import { WallV2Service } from '../../../../../../services/wall-v2.service';
import { WallPlaybackSyncService } from '../../../../../../services/wall-playback-sync.service';
import { VideoService } from '../../../../../../development/video.service';
import { LivekitService } from '../../../../../../development/livekit.service';
import { openFullScreen, sortArrByField } from '../../../../../../helpers/common.helpers';
import { WebrtcPlayerV2Component } from '../../../../../webrtc-player/webrtc-player-v2.component';
import { TimelineDragEvent } from '../../../../../timeline/timeline-series/timeline-series.component';
import { SharedModule } from '../../../../../shared.module';
import { VISIBILITY_CHANGE_INTERVAL } from '../../../../../video/player-base/player-base.component';

/** Half-window (ms) of timeline shown on each side of the playback timestamp. */
const PLAYBACK_WINDOW_MS = 2 * 60 * 60 * 1000;

/** Delay (ms) before triggering play, letting Angular mount the playback players. */
const PLAYBACK_START_DELAY_MS = 1000;
const WALL_TAB_HIDDEN_INACTIVITY_SECONDS = 30;

/** Buffer window (ms) coalescing incoming alert socket events, matching wall-v2-create. */
const ALERT_SOCKET_BUFFER_MS = 1000;

/**
 * Column counts for the standard (non-perfect-square) wall sizes. Everything
 * else falls back to a near-square grid via ceil(sqrt(count)). Keyed by the
 * number of tiles in the active set.
 */
/** Hard cap on grid columns so previews stay legible in the narrow modal panel. */
const MAX_GRID_COLUMNS = 3;

const WALL_GRID_COLUMNS_BY_TILE_COUNT: Record<number, number> = {
  1: 1,
  2: 2,
  3: 3,
  6: 3,
  8: 4,
  10: 5,
  12: 4,
  15: 5,
  20: 5,
  30: 6,
};

/**
 * Camera-preview detail for a single wall, opened either directly by the
 * `view-wall` chat tool or as an overlay from `app-walls-result`. Fetches the wall on its own (via
 * `WallV2Service.getById`) into a local signal, then renders it in one of two
 * modes (`gridViewMode`): a responsive `<webrtc-player>` grid, or the wall's
 * real saved layout via the reused `v2-set-layout` component.
 *
 * NOTE: `v2-set-layout`'s tiles read live/playback + tile settings from the
 * shared `wallV2State` (`selectWallViewTab`/`selectWallPlaybackTs`/
 * `selectWallTileSettings`), so — unlike the grid mode, which is fully local —
 * this component mirrors its state into that store while open and restores the
 * pre-overlay snapshot (`restoreStateSnapshot`) on destroy so the wall
 * create/edit page is never left clobbered. Interactive list drill-downs get
 * the context bar's overlay back-crumb; direct chat results remain history entries.
 */
@Component({
  selector: 'app-wall-camera-result',
  standalone: true,
  imports: [CommonModule, TranslatePipe, MatTooltip, UiPreloaderComponent, SharedModule, WallV2Module],
  templateUrl: './wall-camera-result.component.html',
  styleUrls: ['./wall-camera-result.component.scss'],
  providers: [WallPlaybackSyncService],
})
export class WallCameraResultComponent implements OnInit, OnDestroy {
  private readonly resultsService = inject(IntelligenceResultsService);
  private readonly wallV2Service = inject(WallV2Service);
  private readonly wallPlaybackSyncService = inject(WallPlaybackSyncService);
  private readonly socketMainService = inject(SocketMainService);
  protected readonly videoService = inject(VideoService);
  private readonly livekitService = inject(LivekitService);
  private readonly store$ = inject(Store);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);

  protected readonly PreloaderColor = PreloaderColor;
  protected readonly WallViewTab = WallViewTab;
  protected readonly WallGridViewMode = WallGridViewMode;
  protected readonly timezone: string = moment.tz.guess();

  private readonly fullScreenContainer =
    viewChild<ElementRef<HTMLElement>>('fullScreenContainer');

  /** All mounted tile players, kept in sync with `WallPlaybackSyncService`. */
  private readonly players = viewChildren(WebrtcPlayerV2Component);
  private readonly registeredPlayers = new Set<WebrtcPlayerV2Component>();

  protected readonly camerasLookup: Signal<Dictionary<EdgeCamera.CameraItem>> =
    this.store$.selectSignal(CameraSelectors.selectCamerasLookup);

  /**
   * True when any set of the mirrored wall has alert-configured tiles. Reads the
   * shared slice, so it only flips after `loadWall` dispatches `setSelectedWall`.
   * While true, set switching/rotation is disabled (parity with the wall page,
   * whose alerts fetch is built from the selected set's tiles).
   */
  protected readonly hasWallAlert: Signal<boolean> = computed(
    () => !!this.hasWallAlertRaw(),
  );
  private readonly hasWallAlertRaw: Signal<boolean | undefined> =
    this.store$.selectSignal(WallV2Selectors.selectHasWallAlert);

  /** Incoming alerts of the mirrored wall, newest first — feeds `alerts-incoming-v2`. */
  protected readonly tableAlerts$: Observable<WallV2Model.WallAlert[]> = this.store$
    .select(WallV2Selectors.selectTableAlerts)
    .pipe(map((lookup) => sortArrByField(Object.values(lookup), 'timestamp', 'desc')));

  /** Populated by the `getWallLocations` dispatch in `loadWall`. */
  protected readonly locationLookup$: Observable<{ [key: string]: LocationModel.LocationItem }> =
    this.store$.select(LocationSelectors.selectAllLocationEntities);

  protected readonly wall = signal<WallV2Model.WallMongoDocument | null>(null);
  protected readonly isLoading = signal<boolean>(false);
  protected readonly hasError = signal<boolean>(false);
  protected readonly activeSetIndex = signal<number>(0);
  protected readonly muted = signal<boolean>(true);
  protected readonly wallInactive = signal<boolean>(false);

  /** Simple responsive grid vs. the wall's real saved layout (`v2-set-layout`). */
  protected readonly gridViewMode = signal<WallGridViewMode>(WallGridViewMode.Grid);

  /** Live vs. playback view; playback mounts the timeline and VOD players. */
  protected readonly viewTab = signal<WallViewTab>(WallViewTab.Live);
  protected readonly playbackTs = signal<number | null>(null);
  protected readonly playbackStartTs = signal<number>(0);
  protected readonly playbackEndTs = signal<number>(0);
  protected dragging = false;

  private readonly activeOrganization = this.store$.selectSignal(OrganizationSelectors.selectActiveOrganization);

  /**
   * Pre-overlay copy of the shared wall-v2 slice, captured before this component
   * dispatches anything. Restored on destroy so a wall page open underneath the
   * Cmd+K modal (list/edit/view) gets its state back instead of a wiped slice.
   */
  private readonly wallStateSnapshot: WallV2State = this.store$.selectSignal(WallV2Selectors.selectWallV2State)();

  public constructor() {
    // Raised after the snapshot above is captured, so the restore on destroy
    // clears it. Wall pages hide their canvas while this widget owns the slice.
    this.store$.dispatch(WallV2Actions.setWallWidgetActive({ active: true }));
    // Register/unregister tile players with the sync service as the grid changes.
    effect(() => {
      const current = new Set(this.players());
      for (const player of this.registeredPlayers) {
        if (!current.has(player)) {
          this.wallPlaybackSyncService.unregisterPlayer(player);
          this.registeredPlayers.delete(player);
        }
      }
      for (const player of current) {
        if (!this.registeredPlayers.has(player)) {
          this.wallPlaybackSyncService.registerPlayer(player);
          this.registeredPlayers.add(player);
        }
      }
    });
  }

  @HostListener('document:visibilitychange')
  protected onDocumentVisibilityChange(): void {
    this.checkWallTabVisibility(!!document.hidden);
  }

  /** The wall targeted by the active direct result or interactive overlay (id + name). */
  protected readonly request = computed<IntelligenceModels.Walls.DetailRequest | null>(() => {
    const result = this.resultsService.current();
    if (result?.type !== 'wall-camera') return null;
    return result.data as IntelligenceModels.Walls.DetailRequest;
  });

  protected readonly sets = computed<WallV2Model.WallSet[]>(() => this.wall()?.sets ?? []);
  protected readonly currentSet = computed<WallV2Model.WallSet | null>(
    () => this.sets()[this.activeSetIndex()] ?? null,
  );
  protected readonly tiles = computed<WallV2Model.SetTile[]>(
    () => this.currentSet()?.tiles ?? [],
  );
  protected readonly tileSettings = computed<WallV2Model.TilePreviewSettings | null>(
    () => this.wall()?.settings?.tileSettings ?? null,
  );
  protected readonly gridColumns = computed<number>(() => {
    const count = this.tiles().length;
    if (count <= 0) return 1;
    const columns = WALL_GRID_COLUMNS_BY_TILE_COUNT[count] ?? Math.ceil(Math.sqrt(count));
    return Math.min(MAX_GRID_COLUMNS, columns);
  });

  private rotationTimer: ReturnType<typeof setTimeout> | null = null;
  private wallVisibilityChangeIntervalId: ReturnType<typeof setInterval> | null = null;
  private wallVisibilityChangeTime = 0;
  private loadedWallId: string | null = null;

  public ngOnInit(): void {
    this.loadWall(this.request()?._id ?? null);
    this.initIncomingAlerts();
  }

  public ngOnDestroy(): void {
    this.clearWallVisibilityInactivityTimer();
    this.clearRotation();
    for (const player of this.registeredPlayers) {
      this.wallPlaybackSyncService.unregisterPlayer(player);
    }
    this.registeredPlayers.clear();
    // Leave the shared VideoService back in live mode so the wall page is unaffected.
    if (this.viewTab() === WallViewTab.Playback) {
      this.videoService.OnStop();
      this.videoService.setLive(true);
      this.videoService.setUserPaused(false);
    }
    // Undo the store we populated for layout mode so the wall create/edit page is
    // never left with this overlay's wall / view-tab / playback-ts.
    this.store$.dispatch(WallV2Actions.restoreStateSnapshot({ snapshot: this.wallStateSnapshot }));
  }

  protected trackBySetIndex(index: number): number {
    return index;
  }

  protected trackByTileIndex(index: number): number {
    return index;
  }

  /** Match the production wall renderer's alert-tile detection. */
  protected isAlertTile(tile: WallV2Model.SetTile): boolean {
    return !!tile.allEvents || !!tile.events;
  }

  /** Resolve a camera's display name from the global cameras lookup. */
  protected cameraName(cameraId: string | undefined): string {
    if (!cameraId) return '';
    const camera = this.camerasLookup()[cameraId];
    return camera?.edgeOnly?.name ?? camera?.name ?? '';
  }

  protected selectSet(index: number): void {
    if (index === this.activeSetIndex()) return;
    // Alert walls lock set switching (wall-page parity): the alerts fetch and
    // socket filtering are built from the selected set's tiles.
    if (this.hasWallAlert()) return;
    this.activeSetIndex.set(index);
    // Keep the layout-mode (`v2-set-layout`) store in sync with the active set.
    this.store$.dispatch(WallV2Actions.setSelectedSetIndex({ index }));
    this.scheduleRotation();
  }

  protected toggleMute(): void {
    this.muted.update((muted) => !muted);
  }

  protected editWall(): void {
    const wall = this.wall();
    if (!wall) return;

    this.clearWallInactivity();
    this.resultsService.setOverlay(
      {
        name: wall.name,
        wall,
      },
      'create-wall',
      wall.name,
    );
  }

  /** Open the wall's full view page in a new tab so the Cmd+K session stays open. */
  protected openWallPage(): void {
    const wallId = this.wall()?._id;
    if (!wallId) return;

    const tree = this.router.createUrlTree(['/', routerSegments.wallV2, routerSegments.view, wallId]);
    const path = this.router.serializeUrl(tree);
    const url = window.location?.origin ? `${window.location.origin}${path}` : path;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  protected setGridViewMode(mode: WallGridViewMode): void {
    this.gridViewMode.set(mode);
  }

  /**
   * Switch between live and playback. Mirrors `wall-v2-create`'s view-tab logic
   * but drives state locally (no URL/`wallV2State`). Playback stops any live
   * LiveKit sessions, points the shared `VideoService` at the timestamp, primes
   * the MultiPlayback slice the timeline reads, then starts play after a short
   * delay so the tiles can mount their VOD players.
   */
  protected setViewTab(tab: WallViewTab): void {
    if (tab === this.viewTab()) return;
    this.clearWallInactivity();
    this.viewTab.set(tab);
    // Mirror into the shared store so layout-mode (`v2-set-layout`) tiles follow.
    this.store$.dispatch(WallV2Actions.setWallViewTab({ tab }));

    if (tab === WallViewTab.Live) {
      this.store$.dispatch(WallV2Actions.setWallPlaybackTs({ ts: null }));
      this.videoService.OnStop();
      this.videoService.setLive(true);
      this.videoService.setUserPaused(false);
      this.videoService.play(true);
      this.checkWallTabVisibility(!!document.hidden);
      return;
    }

    const effectiveTs = this.playbackTs() ?? Date.now();
    this.playbackTs.set(effectiveTs);
    this.store$.dispatch(WallV2Actions.setWallPlaybackTs({ ts: effectiveTs }));
    this.playbackStartTs.set(effectiveTs - PLAYBACK_WINDOW_MS);
    this.playbackEndTs.set(Math.max(effectiveTs + PLAYBACK_WINDOW_MS, Date.now()));

    // Live view uses LiveKit; clean all active rooms before switching to playback.
    this.livekitService.cleanAll();
    this.videoService.OnStop();
    this.videoService.setLive(false);
    this.videoService.ts = effectiveTs;
    this.videoService.setUserPaused(false);

    // Prime the MultiPlayback slice the timeline reads for its rows/position.
    const cameras: EdgeCamera.CameraItem[] = this.tiles()
      .map((tile) => tile.camera?.cameraId)
      .filter((cameraId): cameraId is string => !!cameraId)
      .map((cameraId) => this.camerasLookup()[cameraId])
      .filter((camera): camera is EdgeCamera.CameraItem => !!camera);
    this.store$.dispatch(MultiPlaybackActions.setSelectedCameras({ selectedCameras: cameras }));
    this.store$.dispatch(
      MultiPlaybackActions.setMove({ move: { timestamp: effectiveTs, percentage: 0.5 } }),
    );

    setTimeout(() => this.videoService.OnUserPlay(), PLAYBACK_START_DELAY_MS);
  }

  protected onWallSeekStart(): void {
    this.dragging = true;
  }

  protected onWallSeekEnd(_event: TimelineDragEvent): void {
    this.videoService.OnUserPlay();
    this.dragging = false;
  }

  protected onWallTimelineDragStart(): void {
    this.videoService.OnPause();
  }

  protected onWallTimelineDragEnd(event: TimelineDragEvent): void {
    this.playbackStartTs.set(event.start);
    this.playbackEndTs.set(event.end);
  }

  protected onWallPlaybackPlay(): void {
    this.videoService.OnUserPlay();
  }

  protected onWallPlaybackPause(): void {
    this.videoService.OnUserPause();
  }

  protected async fullScreen(): Promise<void> {
    const element = this.fullScreenContainer()?.nativeElement;
    if (!element) return;
    await openFullScreen(element);
  }

  /**
   * Toggle a single camera tile into fullscreen on click. Reuses `openFullScreen`
   * (toggles), so clicking the same tile again — or Esc — exits.
   */
  protected async openTileFullscreen(element: HTMLElement, tile: WallV2Model.SetTile): Promise<void> {
    if (!tile.camera?.cameraId) return;
    await openFullScreen(element);
  }

  protected wallResumePlay(): void {
    this.wallInactive.set(false);
    this.store$.dispatch(SharedActions.setIsInactive({ isInactive: false }));
    this.livekitService.cleanAll();
    this.videoService.setUserPaused(false);
    this.videoService.play(true);
  }

  private loadWall(id: string | null): void {
    if (id === this.loadedWallId) return;
    this.loadedWallId = id;
    this.clearRotation();
    this.wall.set(null);
    this.activeSetIndex.set(0);
    this.hasError.set(false);
    if (!id) return;

    this.isLoading.set(true);
    this.wallV2Service
      .getById(id)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (wall) => {
          this.wall.set(wall);
          this.isLoading.set(false);
          // Mirror the wall into the shared store so layout-mode (`v2-set-layout`)
          // tiles resolve tile settings / camera / location data. Reset on destroy.
          this.store$.dispatch(WallV2Actions.setSelectedWall({ wall }));
          this.store$.dispatch(WallV2Actions.setSelectedSetIndex({ index: 0 }));
          this.store$.dispatch(WallV2Actions.getWallLocations());
          this.scheduleRotation();
          this.checkWallTabVisibility(!!document.hidden);
        },
        error: () => {
          this.isLoading.set(false);
          this.hasError.set(true);
        },
      });
  }

  /**
   * Fetch the wall's incoming alerts and keep them live over the socket. Mirrors
   * `wall-v2-create`'s view-mode pipeline; the overlay is always "view mode", so
   * only the has-alerts gate remains. Store writes land in the shared slice the
   * table reads from and are undone by the `restoreStateSnapshot` on destroy.
   */
  private initIncomingAlerts(): void {
    this.store$
      .select(WallV2Selectors.selectHasWallAlert)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map((hasAlert) => !!hasAlert),
        distinctUntilChanged(),
        filter((hasAlert) => hasAlert),
        tap(() => this.store$.dispatch(WallV2Actions.getTableAlertsByFilters())),
        switchMap(() =>
          this.socketMainService
            .consume<{ result: AlertEntry[] }>(SocketEvents.edgeIncomingAnalyticAlerts)
            .pipe(
              bufferTime(ALERT_SOCKET_BUFFER_MS),
              map((buffered) => buffered.flatMap((event) => event.result || [])),
              filter((alerts) => alerts.length > 0),
              tap((alerts) => this.store$.dispatch(WallV2Actions.alertSocketTriggered({ alerts }))),
            ),
        ),
      )
      .subscribe();
  }

  /**
   * Auto-advance to the next set after the current set's `duration` (in ms, as
   * the full wall page treats it). Chained `setTimeout` so each set honors its
   * own duration; a set with no duration halts rotation, matching wall-v2.
   */
  private scheduleRotation(): void {
    this.clearRotation();
    // No auto-rotation for alert walls — same reason set switching is locked.
    if (this.hasWallAlert()) return;
    const sets = this.sets();
    if (sets.length <= 1) return;
    const duration = this.currentSet()?.duration ?? 0;
    if (!duration) return;

    this.rotationTimer = setTimeout(() => {
      const next = (this.activeSetIndex() + 1) % this.sets().length;
      this.activeSetIndex.set(next);
      this.scheduleRotation();
    }, duration);
  }

  private clearRotation(): void {
    if (this.rotationTimer) {
      clearTimeout(this.rotationTimer);
      this.rotationTimer = null;
    }
  }

  private isWallStreamingView(): boolean {
    return (
      this.viewTab() === WallViewTab.Live &&
      !this.isLoading() &&
      !this.hasError() &&
      this.tiles().some((tile) => !!tile.camera?.cameraId)
    );
  }

  private isInactivityDisabled(): boolean {
    return this.activeOrganization()?.disableInactivity ?? false;
  }

  private checkWallTabVisibility(hidden: boolean): void {
    if (this.isInactivityDisabled() || !this.isWallStreamingView()) {
      return;
    }

    if (hidden) {
      this.startWallVisibilityInactivityTimer();
      return;
    }

    this.clearWallVisibilityInactivityTimer();
  }

  private startWallVisibilityInactivityTimer(): void {
    if (this.wallVisibilityChangeIntervalId || this.wallInactive()) {
      return;
    }

    this.wallVisibilityChangeTime = 0;
    this.wallVisibilityChangeIntervalId = setInterval(() => {
      this.wallVisibilityChangeTime++;
      if (this.wallVisibilityChangeTime >= WALL_TAB_HIDDEN_INACTIVITY_SECONDS) {
        this.pauseLiveForInactivity();
        this.clearWallVisibilityInactivityTimer();
      }
    }, VISIBILITY_CHANGE_INTERVAL);
  }

  private clearWallVisibilityInactivityTimer(): void {
    if (this.wallVisibilityChangeIntervalId) {
      clearInterval(this.wallVisibilityChangeIntervalId);
      this.wallVisibilityChangeIntervalId = null;
    }
    this.wallVisibilityChangeTime = 0;
  }

  private pauseLiveForInactivity(): void {
    if (this.isInactivityDisabled() || !this.isWallStreamingView() || this.wallInactive()) {
      return;
    }

    this.wallInactive.set(true);
    this.videoService.setUserPaused(true);
    this.videoService.OnStop();
    this.livekitService.cleanAll();
  }

  private clearWallInactivity(): void {
    this.clearWallVisibilityInactivityTimer();
    if (!this.wallInactive()) return;
    this.wallInactive.set(false);
    this.videoService.setUserPaused(false);
  }
}
